<script setup lang="ts">
import { computed, useId } from 'vue'

import { useCreditGuard } from '../../composables/useCreditGuard'
import { useCreditStore } from '../../stores/creditStore'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Tooltip } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    locked: number
    charged: number
    maxCredits?: number | null
    /**
     * The host can spend credits on the locked rows (`unlock` is wired). A host that cannot keeps
     * the disabled "coming soon" CTA — this card owns no API, so the unlock call is the host's.
     */
    unlockable?: boolean
    unlocking?: boolean
    /** A viewer cannot spend credits on behalf of the account, so offer the upgrade path. */
    readOnly?: boolean
  }>(),
  { maxCredits: null, unlockable: false, unlocking: false, readOnly: false },
)
const emit = defineEmits<{ unlock: [] }>()

const { t } = useI18n()
const creditStore = useCreditStore()
const creditGuard = useCreditGuard()
const titleId = useId()

const remaining = computed(() => Math.max(0, creditStore.remaining))
const unlockCount = computed(() => Math.min(props.locked, remaining.value))
const canUnlock = computed(
  () => props.unlockable && !props.readOnly && (remaining.value > 0 || props.unlocking),
)
const reachedUserCap = computed(
  () => props.maxCredits !== null && props.charged > 0 && props.charged === props.maxCredits,
)
</script>

<template>
  <section
    v-if="locked > 0"
    class="locked-results-card grid gap-sm p-md border border-warning rounded-md bg-warning-soft text-warning"
    :aria-labelledby="titleId"
  >
    <h3 :id="titleId" class="locked-results-card__title m-0 text-fg text-[0.875rem] font-semibold">
      {{ t('credits.locked.count', { count: locked }) }}
    </h3>
    <div
      class="locked-results-card__body flex items-center justify-between gap-md max-sm:items-start max-sm:flex-col"
    >
      <div class="locked-results-card__message flex items-start gap-xs">
        <Icon name="eye-off" :size="16" aria-hidden="true" />
        <span v-if="reachedUserCap && canUnlock">
          {{
            t('credits.locked.userCapUnlockable', {
              limit: maxCredits ?? 0,
              charged,
              total: charged + locked,
            })
          }}
        </span>
        <span v-else-if="reachedUserCap">
          {{
            t('credits.locked.userCap', {
              limit: maxCredits ?? 0,
              charged,
              total: charged + locked,
            })
          }}
        </span>
        <span v-else-if="canUnlock">
          {{ t('credits.locked.depletedUnlockable', { charged, total: charged + locked }) }}
        </span>
        <span v-else-if="unlockable && remaining === 0">
          {{ t('credits.locked.depletedUpgrade', { charged, total: charged + locked }) }}
        </span>
        <span v-else>
          {{
            t('credits.locked.depleted', {
              charged,
              total: charged + locked,
            })
          }}
        </span>
      </div>

      <Button
        v-if="canUnlock"
        variant="primary"
        size="sm"
        :loading="unlocking"
        :disabled="unlocking"
        data-testid="locked-results-unlock"
        @click="emit('unlock')"
      >
        <template #start><Icon name="bolt" :size="16" aria-hidden="true" /></template>
        {{
          unlocking
            ? t('credits.locked.unlocking')
            : t('credits.locked.unlockMore', { count: unlockCount })
        }}
      </Button>
      <Tooltip
        v-else-if="remaining > 0 && !unlockable"
        :text="t('credits.locked.unlockComingSoon')"
      >
        <Button variant="outline" size="sm" disabled>
          <template #start><Icon name="bolt" :size="16" aria-hidden="true" /></template>
          {{
            t('credits.locked.unlockMore', {
              count: unlockCount,
            })
          }}
        </Button>
      </Tooltip>
      <Button v-else variant="primary" size="sm" @click="creditGuard.openUpgrade">
        {{ t('credits.locked.upgrade') }}
      </Button>
    </div>
  </section>
</template>
